import { useState, type ReactNode, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { api } from '../utils/api-client';

interface ContentGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onContentGenerated?: (content: string) => void;
}

interface Offer {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  merchantName: string | null;
}

export function ContentGenerator({ isOpen, onClose, onContentGenerated }: ContentGeneratorProps): ReactNode {
  useAuthStore(); // Ensure auth context is available
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<'article' | 'review' | 'comparison' | 'guide' | 'listicle'>('article');
  const [targetKeyword, setTargetKeyword] = useState('');
  const [targetWordCount, setTargetWordCount] = useState(1500);
  const [tone, setTone] = useState('professional');
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  const generateMutation = useMutation({
    mutationFn: async (): Promise<{ content: string }> => {
      const selectedOffer = offers.find(o => o.id === selectedOfferId);
      
      if (!selectedOffer) {
        throw new Error('Please select an offer');
      }

      const response = await api.post<{
        content: string;
        contentType: string;
        metadata?: { tokensUsed?: number; durationMs?: number };
      }>('/ai/generate/content', {
        offer: {
          id: selectedOffer.id,
          name: selectedOffer.name,
          description: selectedOffer.description,
          category: selectedOffer.category,
          advertiser: selectedOffer.merchantName,
          payoutType: 'percentage',
          payoutAmount: null,
          currency: 'USD',
          tags: targetKeyword ? [targetKeyword] : [],
        },
        contentType,
        tone,
        targetWordCount,
        keywords: targetKeyword ? [targetKeyword] : [],
        additionalContext: title ? `Title: ${title}` : undefined,
      });

      if (!response?.content) {
        throw new Error('Failed to generate content');
      }

      return { content: response.content };
    },
    onSuccess: (data) => {
      if (onContentGenerated) {
        onContentGenerated(data.content);
      }
      onClose();
    },
  });

  const loadOffers = async (): Promise<void> => {
    setLoadingOffers(true);
    try {
      const response = await api.get<{
        offers?: Offer[];
        pagination?: { total: number; hasMore: boolean };
      }>('/offers?limit=50');
      
      if (response?.offers) {
        setOffers(response.offers);
      }
    } catch (err) {
      console.error('Failed to load offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    void generateMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">AI Content Generator</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title (optional)
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="e.g., Best VPN for 2024"
              />
            </div>

            <div>
              <label htmlFor="contentType" className="block text-sm font-medium text-gray-700">
                Content Type
              </label>
              <select
                id="contentType"
                value={contentType}
                onChange={(e) => setContentType(e.target.value as typeof contentType)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="article">Article</option>
                <option value="review">Review</option>
                <option value="comparison">Comparison</option>
                <option value="guide">Guide</option>
                <option value="listicle">Listicle</option>
              </select>
            </div>

            <div>
              <label htmlFor="offer" className="block text-sm font-medium text-gray-700">
                Affiliate Offer
              </label>
              {offers.length === 0 && !loadingOffers ? (
                <button
                  type="button"
                  onClick={() => void loadOffers()}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                >
                  Load offers...
                </button>
              ) : loadingOffers ? (
                <div className="mt-1 h-10 bg-gray-100 rounded animate-pulse" />
              ) : (
                <select
                  id="offer"
                  value={selectedOfferId}
                  onChange={(e) => setSelectedOfferId(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Select an offer...</option>
                  {offers.map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {offer.name} {offer.merchantName ? `(${offer.merchantName})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="targetKeyword" className="block text-sm font-medium text-gray-700">
                  Target Keyword
                </label>
                <input
                  type="text"
                  id="targetKeyword"
                  value={targetKeyword}
                  onChange={(e) => setTargetKeyword(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g., best vpn"
                />
              </div>

              <div>
                <label htmlFor="targetWordCount" className="block text-sm font-medium text-gray-700">
                  Word Count
                </label>
                <input
                  type="number"
                  id="targetWordCount"
                  value={targetWordCount}
                  onChange={(e) => setTargetWordCount(Number.parseInt(e.target.value, 10))}
                  min={500}
                  max={5000}
                  step={100}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="tone" className="block text-sm font-medium text-gray-700">
                Tone
              </label>
              <select
                id="tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="friendly">Friendly</option>
                <option value="authoritative">Authoritative</option>
                <option value="conversational">Conversational</option>
              </select>
            </div>

            {generateMutation.error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3">
                <p className="text-sm text-red-600">
                  {generateMutation.error instanceof Error ? generateMutation.error.message : 'Failed to generate content'}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={generateMutation.isPending || !selectedOfferId}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {generateMutation.isPending ? 'Generating...' : 'Generate Content'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
